import { useCallback, useEffect, useRef } from 'react';

import { type Rive } from '@rive-app/react-canvas';
import type { TFunction } from 'i18next';

import type { LoyaltyTierKey } from '../../../../stores/loyalty/types';
import { useStores } from '../../../../stores/useStores';
import { fireTrigger, setBool, setEnum, setNumber, setString } from '../../../../utils/riveHelper';

import { bindCountdown, bindDayCards, bindMonthlyToolTips, bindTopBanner } from './dailyBonusRiveHelpers';

export function useDailyBonusRiveBindings(rive: Rive | null, t: TFunction, isScModeAvailable: boolean) {
  const { rewardsStore, loyaltyProgramStore, userStore } = useStores();
  const hasBoundRef = useRef(false);
  const initialDayRef = useRef<number | null>(null);

  // -----------------------------------------------------------------------
  // Initial binding — maps all store data into Rive VMs, then plays
  // -----------------------------------------------------------------------
  const bindAndPlay = useCallback(() => {
    if (!rive) return;

    const vm = rive.viewModelInstance;
    if (!vm) return;

    const dailyBonus = rewardsStore.dailyBonus;
    if (!dailyBonus) return;

    const { currentDay, monthlyBonusDay, timeUntilNextBonusMS, isEntitledToBonus } = dailyBonus;

    if (hasBoundRef.current) {
      const claimedDay = initialDayRef.current! + 1;
      const popupVM = vm.viewModel?.('popup');
      const dayVM = popupVM?.viewModel?.(`day${claimedDay}`);
      if (dayVM) {
        setBool(dayVM, 'isClaimed', currentDay >= claimedDay);
      }
      return;
    }

    initialDayRef.current = currentDay;

    // Root VM
    setNumber(vm, 'currentDay', currentDay + 1);
    setBool(vm, 'isEntitledToBonus', isEntitledToBonus);
    setString(vm, 'nextBonus', t('bonus.nextBonus'));
    bindCountdown(vm, timeUntilNextBonusMS);
    setString(vm, 'tapToCollect', t('bonus.tapToCollect'));

    const popupVM = vm.viewModel?.('popup');
    if (!popupVM) return;

    // Tier
    const tierKey = loyaltyProgramStore.userLoyalty?.currentTier?.key;
    if (tierKey) setEnum(popupVM, 'tier', tierKey.toUpperCase());

    // Dynasty
    setBool(popupVM, 'isDynasty', !!userStore.profile?.hasVipHost);

    // Day cards, top banner, countdown
    bindDayCards(popupVM, rewardsStore.dailyBonusesList, currentDay, isScModeAvailable, t);
    bindTopBanner(
      popupVM,
      isEntitledToBonus,
      monthlyBonusDay,
      rewardsStore.monthlyBonusesList,
      tierKey as LoyaltyTierKey,
      t,
    );

    // Monthly prize tooltips (on root VM)
    bindMonthlyToolTips(vm, rewardsStore.monthlyBonusesList, monthlyBonusDay, isScModeAvailable, t);

    hasBoundRef.current = true;
    rive.play();
    fireTrigger(vm, 'activate');
    setBool(vm, 'waitForClaim', isEntitledToBonus);
  }, [
    rive,
    rewardsStore.dailyBonus,
    rewardsStore.dailyBonusesList,
    rewardsStore.monthlyBonusesList,
    loyaltyProgramStore.userLoyalty,
    userStore.profile,
    isScModeAvailable,
    t,
  ]);

  useEffect(() => {
    bindAndPlay();
  }, [bindAndPlay]);

  // -----------------------------------------------------------------------
  // Countdown — tick every second
  // -----------------------------------------------------------------------

  useEffect(() => {
    if (!rive) return;

    const id = setInterval(() => {
      rewardsStore.countDownBonusTime();

      const timeLeft = rewardsStore.dailyBonus?.timeUntilNextBonusMS ?? 0;
      const vm = rive.viewModelInstance;
      if (!vm) return;

      bindCountdown(vm, timeLeft);
    }, 1_000);

    return () => {
      clearInterval(id);
    };
  }, [rive, rewardsStore]);

  // -----------------------------------------------------------------------
  // Imperative actions exposed to the component
  // -----------------------------------------------------------------------

  const fireClaim = useCallback(() => {
    const vm = rive?.viewModelInstance;
    return vm ? fireTrigger(vm, 'claim') : false;
  }, [rive]);

  /**
   * Check if `newMonthlyDay` is a milestone. If so, bind reward data
   * to the gift animation VM and fire it. Returns true when triggered.
   */
  const checkAndFireMonthlyBonus = useCallback(
    (newMonthlyDay: number) => {
      if (!rive) return false;

      const isEntitledToBonus = rewardsStore.dailyBonus?.isEntitledToBonus ?? false;
      const monthlyBonuses = rewardsStore.monthlyBonusesList;
      const milestoneReward = monthlyBonuses.find((item) => item.day === newMonthlyDay);
      if (!milestoneReward) return false;

      const vm = rive.viewModelInstance;
      const popupVM = vm?.viewModel?.('popup');
      if (!popupVM) return false;

      const tierKey = loyaltyProgramStore.userLoyalty?.currentTier?.key;
      bindTopBanner(popupVM, isEntitledToBonus, newMonthlyDay, monthlyBonuses, tierKey as LoyaltyTierKey, t);
      bindMonthlyToolTips(vm, monthlyBonuses, newMonthlyDay, isScModeAvailable, t);

      const giftVM = vm?.viewModel?.('giftAnimations');
      if (!giftVM) return false;

      // boxNum expects 1..N index based on sorted position
      const sortedDays = monthlyBonuses.map((b) => b.day).sort((a, b) => a - b);
      const milestoneIndex = sortedDays.indexOf(newMonthlyDay) + 1;
      setNumber(giftVM, 'boxNum', milestoneIndex);

      fireTrigger(vm, 'boxOpen');
      return fireTrigger(giftVM, 'claimMonthlyGift');
    },
    [
      rive,
      rewardsStore.dailyBonus,
      rewardsStore.monthlyBonusesList,
      loyaltyProgramStore.userLoyalty,
      isScModeAvailable,
      t,
    ],
  );

  return { fireClaim, checkAndFireMonthlyBonus };
}
